import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { formatDateRu } from "../../../utils/appointmentLabels";
import s from "../clinic.module.css";
import { Button, Field, Textarea, useToast } from "../../ui";

function fmtNoteDate(iso) {
  if (!iso) return "";
  return formatDateRu(iso, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function sessionLabel(note) {
  if (!note.session?.start_datetime) return null;
  const dt = note.session.start_datetime;
  const date = formatDateRu(dt, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const group = note.session.group?.name;
  return group ? `${date} · ${group}` : date;
}

export default function ClientNotesSection({ patientId, legacyNote, previewLimit = null }) {
  const toast = useToast();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBody, setNewBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editBody, setEditBody] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clinicAPI.getClientNotes(patientId);
      setNotes(data.notes || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить заметки");
    } finally {
      setLoading(false);
    }
  }, [patientId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const addNote = async (e) => {
    e.preventDefault();
    const body = newBody.trim();
    if (!body) return;
    setSaving(true);
    try {
      await clinicAPI.createClientNote(patientId, { body });
      setNewBody("");
      await load();
      toast.success("Заметка добавлена");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (noteId) => {
    const body = editBody.trim();
    if (!body) return;
    setSaving(true);
    try {
      await clinicAPI.updateClientNote(patientId, noteId, { body });
      setEditId(null);
      setEditBody("");
      await load();
      toast.success("Заметка обновлена");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const removeNote = async (note) => {
    if (!note?.id) return;
    if (!window.confirm("Удалить эту заметку?")) return;
    setSaving(true);
    try {
      await clinicAPI.deleteClientNote(patientId, note.id);
      if (editId === note.id) {
        setEditId(null);
        setEditBody("");
      }
      await load();
      toast.success("Заметка удалена");
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось удалить");
    } finally {
      setSaving(false);
    }
  };

  const visibleNotes =
    previewLimit != null && previewLimit > 0
      ? notes.slice(0, previewLimit)
      : notes;
  const hiddenCount =
    previewLimit != null && previewLimit > 0
      ? Math.max(0, notes.length - previewLimit)
      : 0;

  return (
    <div className={s.clientProfileTabPanel}>
      {previewLimit == null ? (
        <p className={s.clientTabMuted}>
          Долгосрочные записи о клиенте. Видны только тренерам и администраторам.
        </p>
      ) : null}

      {legacyNote?.trim() && notes.length === 0 && !loading ? (
        <div className={`${s.clientTabCard} ${s.clientNoteLegacy}`}>
          <p className={s.clientNoteLegacyLabel}>Старая заметка из карточки</p>
          <p className={s.clientNoteBody}>{legacyNote.trim()}</p>
          <p className={s.clientNoteMeta}>
            Перенесите текст в новую запись ниже — поле на вкладке «Основное»
            скоро будет убрано.
          </p>
        </div>
      ) : null}

      <section className={s.clientTabCard}>
        {previewLimit == null ? (
          <>
            <h3 className={s.clientTabCardTitle}>Новая заметка</h3>
            <form className={s.clientTabFormStack} onSubmit={addNote}>
              <Field label="Текст">
                <Textarea
                  rows={3}
                  value={newBody}
                  onChange={(e) => setNewBody(e.target.value)}
                  placeholder="Особенности, договорённости с родителями, напоминания…"
                />
              </Field>
              <Button type="submit" size="sm" disabled={saving || !newBody.trim()}>
                {saving ? "…" : "Добавить заметку"}
              </Button>
            </form>
          </>
        ) : null}
      </section>

      <section className={s.clientTabCard}>
        <h3 className={s.clientTabCardTitle}>
          {previewLimit != null ? "Недавние записи" : "История"}
        </h3>
        {loading ? (
          <p className={s.clientTabMuted}>Загрузка…</p>
        ) : visibleNotes.length === 0 ? (
          <p className={s.clientTabMuted}>Заметок пока нет</p>
        ) : (
          <ul className={s.clientNoteList}>
          {visibleNotes.map((note) => {
            const editing = editId === note.id;
            const sessionTag = sessionLabel(note);
            return (
              <li key={note.id} className={s.clientNoteItem}>
                <div className={s.clientNoteItemHead}>
                  <div>
                    <span className={s.clientNoteAuthor}>
                      {note.author?.full_name || "Сотрудник"}
                    </span>
                    <span className={s.clientNoteDate}>
                      {fmtNoteDate(note.created_at)}
                    </span>
                    {note.edited_at ? (
                      <span className={s.clientNoteEdited}>
                        изменено{" "}
                        {note.editor?.full_name
                          ? `${note.editor.full_name}, `
                          : ""}
                        {fmtNoteDate(note.edited_at)}
                      </span>
                    ) : null}
                  </div>
                  {!editing ? (
                    <div className={s.clientNoteEditActions}>
                      <button
                        type="button"
                        className={s.clientNoteEditBtn}
                        onClick={() => {
                          setEditId(note.id);
                          setEditBody(note.body);
                        }}
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        className={s.clientNoteEditBtn}
                        disabled={saving}
                        onClick={() => removeNote(note)}
                      >
                        Удалить
                      </button>
                    </div>
                  ) : null}
                </div>
                {sessionTag ? (
                  <span className={s.clientNoteSessionTag}>
                    Занятие: {sessionTag}
                  </span>
                ) : null}
                {editing ? (
                  <div className={s.clientNoteEditForm}>
                    <Textarea
                      rows={3}
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                    />
                    <div className={s.clientNoteEditActions}>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEditId(null);
                          setEditBody("");
                        }}
                      >
                        Отмена
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={saving || !editBody.trim()}
                        onClick={() => saveEdit(note.id)}
                      >
                        {saving ? "…" : "Сохранить"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className={s.clientNoteBody}>{note.body}</p>
                )}
              </li>
            );
          })}
          </ul>
        )}
        {hiddenCount > 0 ? (
          <p className={s.clientTabMuted}>
            Ещё {hiddenCount} {hiddenCount === 1 ? "заметка" : "заметок"} — нажмите «Все заметки» выше
          </p>
        ) : null}
      </section>
    </div>
  );
}
